import React, { useState } from "react";
import * as hrmsService from "../../../services/hrmsService";
import { monthBounds, shiftMonth } from "../leaveCalendar";
import CorrectionDrawer from "./CorrectionDrawer";
import PunchCard from "./PunchCard";
import { EmptyState, LoadState } from "../hrComponents";
import { cardStyle, formatDate, formatMinutes, localTime, statusBadge, statusLabel, tdStyle, thStyle, todayYmd, useLoad, attendanceStatus, badgeStyle } from "../hrUi";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function Stat({ label, value, warn }) {
  return (
    <div style={{ ...cardStyle, padding: "10px 16px", minWidth: 110 }}>
      <div style={{ fontSize: 12, color: "#6B7280" }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: warn ? "#B45309" : "#111827" }}>{value}</div>
    </div>
  );
}

// My days for a month, my totals, and a way to ask for a correction.
export default function MyAttendance() {
  const [month, setMonth] = useState(monthBounds(todayYmd()).from);
  const [fixing, setFixing] = useState(null);
  const { from, to } = monthBounds(month);
  const days = useLoad(() => hrmsService.getMyAttendance(from, to), [from, to]);
  const corrections = useLoad(() => hrmsService.listCorrections({ mine: 1 }), []);
  const [y, m] = month.split("-").map(Number);
  const rows = [...(days.data?.days || [])].reverse();
  const s = days.data?.summary;

  return (
    <>
      <PunchCard />

      <div className="d-flex align-items-center gap-2 mt-4 mb-3">
        <button type="button" className="btn btn-outline-secondary btn-sm" aria-label="Previous month" onClick={() => setMonth(shiftMonth(month, -1))}>
          ‹
        </button>
        <strong style={{ minWidth: 140, textAlign: "center" }}>
          {MONTHS[m - 1]} {y}
        </strong>
        <button type="button" className="btn btn-outline-secondary btn-sm" aria-label="Next month" onClick={() => setMonth(shiftMonth(month, 1))}>
          ›
        </button>
      </div>

      {s ? (
        <div className="d-flex flex-wrap gap-2 mb-3">
          <Stat label="Present" value={s.present} />
          <Stat label="Half days" value={s.half_days} />
          <Stat label="Absent" value={s.absent} warn={s.absent > 0} />
          <Stat label="On leave" value={s.on_leave} />
          <Stat label="Late marks" value={s.late_marks_counted} warn={s.late_marks_counted > 0} />
          <Stat label="Overtime" value={formatMinutes(s.ot_minutes)} />
        </div>
      ) : null}

      <LoadState loading={days.loading} error={days.error} onRetry={days.reload}>
        {rows.length === 0 ? (
          <EmptyState title="No attendance for this month yet" text="Days appear here the morning after they finish." />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  {["Date", "Status", "In", "Out", "Worked", "Late", "Overtime", ""].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => {
                  const st = attendanceStatus(d.status);
                  return (
                    <tr key={d.att_date}>
                      <td style={tdStyle}>{formatDate(d.att_date)}</td>
                      <td style={tdStyle}>
                        <span style={badgeStyle(st.bg, st.color)}>{st.label}</span>
                        {d.flags ? <div style={{ fontSize: 11, color: "#6B7280" }}>{d.flags.split(",").map((f) => statusLabel(f)).join(", ")}</div> : null}
                      </td>
                      <td style={tdStyle}>{localTime(d.first_in_local)}</td>
                      <td style={tdStyle}>{localTime(d.last_out_local)}</td>
                      <td style={tdStyle}>{formatMinutes(d.net_minutes)}</td>
                      <td style={tdStyle}>{d.late_minutes ? formatMinutes(d.late_minutes) : "—"}</td>
                      <td style={tdStyle}>{d.ot_minutes ? formatMinutes(d.ot_minutes) : "—"}</td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        {!d.is_frozen && ["ABSENT", "MISSING_PUNCH", "HALF_DAY", "LOP"].includes(d.status) ? (
                          <button type="button" className="btn btn-link btn-sm" onClick={() => setFixing(d.att_date)}>
                            Correct
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>

      <h6 className="mt-4 mb-2">My correction requests</h6>
      <LoadState loading={corrections.loading} error={corrections.error} onRetry={corrections.reload} rows={2}>
        {(corrections.data || []).length === 0 ? (
          <EmptyState title="No correction requests" />
        ) : (
          <div style={{ ...cardStyle, padding: "4px 16px" }}>
            {corrections.data.slice(0, 10).map((c) => (
              <div key={c.id} className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: "1px solid #F3F4F6", fontSize: 14 }}>
                <span>
                  {formatDate(c.att_date)} · {statusLabel(c.kind)}
                </span>
                <span style={statusBadge(c.status)}>{statusLabel(c.status)}</span>
              </div>
            ))}
          </div>
        )}
      </LoadState>

      <CorrectionDrawer date={fixing} onHide={() => setFixing(null)} onDone={() => { corrections.reload(); days.reload(); }} />
    </>
  );
}
